import * as React from "react";

const MOBILE_BREAKPOINT = 768;

export function useIsMobile() {
  const [isMobile, setIsMobile] = React.useState<boolean | undefined>(
    undefined,
  );

  React.useEffect(() => {
    const mql = window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT - 1}px)`);
    const onChange = () => {
      setIsMobile(window.innerWidth < MOBILE_BREAKPOINT);
    };
    mql.addEventListener("change", onChange);
    setIsMobile(window.innerWidth < MOBILE_BREAKPOINT);
    return () => mql.removeEventListener("change", onChange);
  }, []);

  return !!isMobile;
}

const HOVER_POINTER_QUERY = "(hover: hover) and (pointer: fine)";

function subscribeToHoverPointer(onChange: () => void) {
  const mql = window.matchMedia(HOVER_POINTER_QUERY);
  mql.addEventListener("change", onChange);
  return () => mql.removeEventListener("change", onChange);
}

/**
 * True when the primary pointer can hover and is precise (mouse/trackpad).
 * False on touch-first devices regardless of viewport width, so hover-driven
 * affordances can stay available in narrow desktop windows.
 *
 * Read synchronously on mount (server snapshot false) so gated chrome renders
 * on the first client paint: the session switcher hint must not blink out for
 * a frame when the sidebar skeleton swaps to the live sidebar.
 */
export function useHasHoverPointer() {
  return React.useSyncExternalStore(
    subscribeToHoverPointer,
    () => window.matchMedia(HOVER_POINTER_QUERY).matches,
    () => false,
  );
}

/** True when viewport width is strictly less than `maxWidthExclusive` (same semantics as `useIsMobile`). */
export function useIsMaxWidth(maxWidthExclusive: number) {
  const [matches, setMatches] = React.useState<boolean | undefined>(undefined);

  React.useEffect(() => {
    const mql = window.matchMedia(`(max-width: ${maxWidthExclusive - 1}px)`);
    const onChange = () => {
      setMatches(window.innerWidth < maxWidthExclusive);
    };
    mql.addEventListener("change", onChange);
    setMatches(window.innerWidth < maxWidthExclusive);
    return () => mql.removeEventListener("change", onChange);
  }, [maxWidthExclusive]);

  return !!matches;
}
